import {
  MDM_PROPERTIES,
  MDM_TYPES,
  type MdmProperty,
  type MdmType,
} from '@/constants/mdm';

export const HIDDEN = 'hidden';
export const OLAPS_ONLY = 'aggregates_only';
export const SHARING = 'full';
export const GREENFIELD_SHARING = 'greenfield_full';

// Sharing types
export const SHARING_TYPES = {
  GREENFIELD_SHARING,
  SHARING,
  OLAPS_ONLY,
  HIDDEN,
} as const;

export type SharingType = (typeof SHARING_TYPES)[keyof typeof SHARING_TYPES];

export const POPULATION_RULE = 'population';
export const PARTNER_RULE = 'partner';
export const PARTNER_POPULATION_RULE = 'partner_population';

export const RULE_TYPES = {
  POPULATION_RULE,
  PARTNER_RULE,
  PARTNER_POPULATION_RULE,
} as const;

export type RuleType = (typeof RULE_TYPES)[keyof typeof RULE_TYPES];

const FULL_TEXT = {
  title: 'Sharing',
  selectorTitle: 'Sharing data',
  value: SHARING,
  cssClasses: [
    'bg-success-background-weak',
    'border-success-text-strong',
    'text-success-text-strong',
  ],
  variantClasses: ['hover:border-success-text-strong'],
  subtitle: ({
    partnerName = 'Your partners',
    populationName = 'this population',
    showSubject = true,
  } = {}) =>
    `${showSubject ? partnerName : ''} can see the data shared
    with them for all overlaps with ${populationName}.`,
};

export const SHARING_ACTIVE_CLASSES: Record<SharingType, string> = {
  [GREENFIELD_SHARING]:
    'border-info-accent text-info-text bg-info-background-weak',
  [SHARING]:
    'border-success-accent text-success-text bg-success-background-weak',
  [OLAPS_ONLY]:
    'border-warning-accent text-warning-text bg-warning-background-weak',
  [HIDDEN]: 'border-danger-accent text-danger-text bg-danger-background-weak',
};

export const SELECTABLE_VISIBILITY_OPTIONS = [
  {
    name: 'Share Overlaps',
    value: SHARING,
    activeClasses: SHARING_ACTIVE_CLASSES[SHARING],
  },
  {
    name: 'Counts',
    value: OLAPS_ONLY,
    activeClasses: SHARING_ACTIVE_CLASSES[OLAPS_ONLY],
  },
  {
    name: 'Hidden',
    value: HIDDEN,
    activeClasses: SHARING_ACTIVE_CLASSES[HIDDEN],
  },
];

export const VISIBILITY_OPTIONS_TEXT = {
  full: FULL_TEXT,
  greenfield_full: { ...FULL_TEXT, value: GREENFIELD_SHARING },
  use_defaults: {
    title: 'Use Defaults',
    value: 'use_defaults',
    subtitle: ({
      partnerName = 'Your partners',
      populationName = 'this population',
      showSubject = true,
    } = {}) =>
      `${showSubject ? partnerName : ''} will be subject to
      the sharing defaults defined for ${populationName}.`,
  },
  aggregates_only: {
    title: 'Counts',
    value: OLAPS_ONLY,
    cssClasses: [
      'bg-accent-background-weak',
      'border-accent-text-strong',
      'text-warning-text-strong',
    ],
    variantClasses: ['hover:border-accent-text-strong'],
    subtitle: ({
      partnerName = 'Your partners',
      populationName = 'this population',
      showSubject = true,
      isTerse = false,
    } = {}) =>
      !isTerse
        ? `${showSubject ? partnerName : ''} can see a summary
      count of overlaps for ${populationName}. No specifics about
      the overlaps, including name or any other data, is shared.`
        : `${showSubject ? partnerName : ''} can see a summary
        count of overlaps for ${populationName}, but specifics
        are not shared.`,
  },
  hidden: {
    title: 'Hidden',
    value: HIDDEN,
    cssClasses: [
      'bg-danger-background-weak',
      'border-danger-text',
      'text-danger-text-strong',
    ],
    variantClasses: ['hover:border-danger-text'],
    subtitle: ({
      partnerName = 'Your partners',
      populationName = 'this population',
      showSubject = true,
    } = {}) =>
      `${showSubject ? partnerName : ''} cannot see
      ${populationName}.`,
  },
};

export const SHARING_LEVEL_OPTIONS_TEXT = {
  use_defaults: {
    title: 'Use Defaults',
    subtitle: (companyName: string) => `${companyName} will see your populations
    based on their sharing defaults`,
  },
  aggregates_only: {
    title: 'Overlap Counts Only',
    subtitle: (
      companyName: string,
    ) => `${companyName} will only see overlap counts for your populations.
    No data will be shared.`,
  },
  'set-per-pop': {
    title: 'Custom',
    subtitle: (companyName: string) =>
      `Set specific population sharing levels for ${companyName}`,
  },
};

export const CUSTOM = 'custom';
export const RECOMMENDED = 'recommended';

export const CROSSBEAM_DEFAULT_LABEL = 'Crossbeam Default';
export const CROSSBEAM_PRESET_LABEL = 'Crossbeam Preset';
export const PRESET_NAME_MAX_LENGTH = 40;

/* Identifies the Crossbeam preset by what it is rather than what it is called.
   Named presets are filtered by their display name, which is a deliberate
   label match across feeds, but the Crossbeam preset has no name of its own. */
export const CROSSBEAM_DEFAULT_PRESET_TYPE = 'crossbeam_default';

export const PRESET_FIELDS_LOOKUP: Record<MdmType, MdmProperty[]> = {
  [MDM_TYPES.ACCOUNT]: [
    MDM_PROPERTIES.account.BILLING_COUNTRY,
    MDM_PROPERTIES.account.CREATED_AT,
    MDM_PROPERTIES.account.EMPLOYEES,
    MDM_PROPERTIES.account.INDUSTRY,
    MDM_PROPERTIES.account.NAME,
    MDM_PROPERTIES.account.TYPE,
    MDM_PROPERTIES.account.WEBSITE,
  ],
  [MDM_TYPES.LEAD]: [
    MDM_PROPERTIES.lead.CREATED_AT,
    MDM_PROPERTIES.lead.EMAIL,
    MDM_PROPERTIES.lead.NAME,
    MDM_PROPERTIES.lead.PHONE,
  ],
  [MDM_TYPES.DEAL]: [
    MDM_PROPERTIES.deal.CLOSE_DATE,
    MDM_PROPERTIES.deal.IS_CLOSED,
    MDM_PROPERTIES.deal.IS_WON,
    MDM_PROPERTIES.deal.LAST_ACTIVITY_AT,
    MDM_PROPERTIES.deal.NAME,
    MDM_PROPERTIES.deal.OPEN_DATE,
    MDM_PROPERTIES.deal.STAGE,
  ],
  [MDM_TYPES.USER]: [
    MDM_PROPERTIES.user.EMAIL,
    MDM_PROPERTIES.user.FIRST_NAME,
    MDM_PROPERTIES.user.NAME,
    MDM_PROPERTIES.user.LAST_NAME,
    MDM_PROPERTIES.user.TITLE,
  ],
  [MDM_TYPES.CONTACT]: [
    MDM_PROPERTIES.contact.CREATED_AT,
    MDM_PROPERTIES.contact.EMAIL,
    MDM_PROPERTIES.contact.FIRST_NAME,
    MDM_PROPERTIES.contact.LAST_ACTIVITY_AT,
    MDM_PROPERTIES.contact.LAST_NAME,
    MDM_PROPERTIES.contact.NAME,
    MDM_PROPERTIES.contact.TITLE,
  ],
  [MDM_TYPES.DEAL_CONTACT]: [
    MDM_PROPERTIES.deal_contact_role.IS_PRIMARY,
    MDM_PROPERTIES.deal_contact_role.ROLE,
  ],
  [MDM_TYPES.PRODUCT]: [MDM_PROPERTIES.product.ID, MDM_PROPERTIES.product.NAME],
  [MDM_TYPES.OPPORTUNITY_LINE_ITEM]: [MDM_PROPERTIES.deal_product.ID],
  [MDM_TYPES.PARTNER_CANDIDATE]: [],
  [MDM_TYPES.ACCOUNT_PRODUCT]: [],
} as const;

export const NEW_SHARING_TYPES_MAP = {
  [GREENFIELD_SHARING]: {
    label: 'All Accounts',
    value: GREENFIELD_SHARING,
    tag: 'Recommended for pipe gen',
    description:
      'Partners can see every account in this population, including those that do not overlap',
  },
  [SHARING]: {
    label: 'Overlapping Accounts',
    tag: 'Required for mapping',
    value: SHARING,
    description: 'Partners can see the list of accounts you both share',
  },
  [OLAPS_ONLY]: {
    label: 'Counts Only',
    value: OLAPS_ONLY,
    description: 'Partners only see how many accounts you have in common',
  },
  [HIDDEN]: {
    label: 'Hidden',
    value: HIDDEN,
    description: 'Population remains fully private',
  },
} as const;
